import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  getDraftAssignedDates,
  getPatternDates,
  getRemainingCapacityForDate,
  scoreWorkerForShift,
} from "./workerAssignmentHelpers";

function AssignWorkerModal({
  isOpen,
  toggle,
  worker,
  pattern,
  shiftIndex,
  draftAssignments,
  onConfirm,
}) {
  const shiftDates = useMemo(() => getPatternDates(pattern), [pattern]);
  const overlap = useMemo(() => {
    if (!worker) return [];
    return scoreWorkerForShift(worker, pattern, shiftDates).overlappingDates;
  }, [worker, pattern, shiftDates]);

  const existingDraftDates = useMemo(() => {
    if (!worker) return [];
    return getDraftAssignedDates(draftAssignments, shiftIndex, worker.slug);
  }, [draftAssignments, shiftIndex, worker]);

  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    if (isOpen && worker) {
      setSelectedDates(existingDraftDates.filter((d) => overlap.includes(d)));
    }
  }, [isOpen, worker, existingDraftDates, overlap]);

  const dateOptions = useMemo(() => {
    return shiftDates.map((dateIso) => {
      const remaining = getRemainingCapacityForDate(
        pattern,
        shiftIndex,
        dateIso,
        draftAssignments
      );
      const alreadyDraftedHere = existingDraftDates.includes(dateIso);
      const available = overlap.includes(dateIso);
      // Selecting a date the worker already holds does not consume extra capacity
      const hasCapacity = remaining > 0 || alreadyDraftedHere;
      return {
        dateIso,
        available,
        remaining,
        hasCapacity,
        disabled: !available || !hasCapacity,
      };
    });
  }, [
    shiftDates,
    pattern,
    shiftIndex,
    draftAssignments,
    overlap,
    existingDraftDates,
  ]);

  const toggleDate = (dateIso) => {
    setSelectedDates((prev) =>
      prev.includes(dateIso)
        ? prev.filter((d) => d !== dateIso)
        : [...prev, dateIso]
    );
  };

  const selectAllAssignable = () => {
    setSelectedDates(
      dateOptions.filter((d) => !d.disabled).map((d) => d.dateIso)
    );
  };

  const clearAll = () => setSelectedDates([]);

  if (!worker) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered>
      <ModalHeader toggle={toggle}>
        Assign {worker.fullName}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Choose one or more dates for this shift. Full dates and unavailable
          days are disabled.
        </p>

        <div className="d-flex gap-2 mb-3">
          <Button color="light" size="sm" type="button" onClick={selectAllAssignable}>
            Select available
          </Button>
          <Button color="light" size="sm" type="button" onClick={clearAll}>
            Clear
          </Button>
        </div>

        <div className="br-wa-date-picker">
          {dateOptions.length === 0 ? (
            <div className="text-muted">No dates on this shift.</div>
          ) : (
            dateOptions.map((opt) => (
              <FormGroup check className="br-wa-date-picker__row" key={opt.dateIso}>
                <Input
                  type="checkbox"
                  id={`br-wa-date-${worker.slug}-${opt.dateIso}`}
                  checked={selectedDates.includes(opt.dateIso)}
                  disabled={opt.disabled && !selectedDates.includes(opt.dateIso)}
                  onChange={() => toggleDate(opt.dateIso)}
                />
                <Label
                  check
                  for={`br-wa-date-${worker.slug}-${opt.dateIso}`}
                  className="br-wa-date-picker__label"
                >
                  <span>{dayjs(opt.dateIso).format("ddd, DD MMM YYYY")}</span>
                  <span className="br-wa-date-picker__status">
                    {!opt.available ? (
                      <Badge className="bg-secondary">Unavailable</Badge>
                    ) : !opt.hasCapacity ? (
                      <Badge className="bg-warning text-dark">Full</Badge>
                    ) : (
                      <Badge className="bg-success">
                        {opt.remaining} left
                      </Badge>
                    )}
                  </span>
                </Label>
              </FormGroup>
            ))
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            onConfirm(selectedDates);
            toggle();
          }}
        >
          Save dates
        </Button>
      </ModalFooter>
    </Modal>
  );
}

AssignWorkerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  worker: PropTypes.object,
  pattern: PropTypes.object,
  shiftIndex: PropTypes.number.isRequired,
  draftAssignments: PropTypes.arrayOf(PropTypes.object).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

AssignWorkerModal.defaultProps = {
  worker: null,
  pattern: null,
};

export default AssignWorkerModal;
